import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000/api/v1';

const getAuthHeaders = (token) => ({
    headers: { Authorization: `Bearer ${token}` }
});

/**
 * Updates the feature flags for a specific user. Superadmin only.
 * @param {string} token - The superadmin's authentication token.
 * @param {string} userId - The ID of the user to update.
 * @param {string[]} flags - The new array of feature flags for the user.
 * @returns {Promise<object>} - A promise that resolves to the updated user data.
 */
export const updateUserFeatureFlags = async (token, userId, flags) => {
    // --- THIS IS THE FIX ---
    // The URL now correctly includes the '/admin' prefix to match the backend router.
    const response = await axios.put(
        `${API_BASE_URL}/admin/users/${userId}/features`,
        { feature_flags: flags },
        getAuthHeaders(token)
    );
    // --- END OF FIX ---
    return response.data;
};



export const getAllUsersStats = async (token, params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.search) queryParams.append('search', params.search);
    queryParams.append('page', String(params.page || 1));
    queryParams.append('limit', String(params.limit || 10));
    if (params.sortBy) queryParams.append('sortBy', params.sortBy);
    if (params.sortDir) queryParams.append('sortDir', params.sortDir);
    if (params.lastLoginFilter) queryParams.append('lastLoginFilter', params.lastLoginFilter);

    const response = await axios.get(`${API_BASE_URL}/admin/users?${queryParams.toString()}`, getAuthHeaders(token));
    return { users: response.data.data, total_count: response.data.totalCount };
};

export const getAllConversations = async (token, params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.search) queryParams.append('search', params.search);
    queryParams.append('page', String(params.page || 1));
    queryParams.append('limit', String(params.limit || 10));
    if (params.sortBy) queryParams.append('sortBy', params.sortBy);
    if (params.sortDir) queryParams.append('sortDir', params.sortDir);
    if (params.dateFilter) queryParams.append('dateFilter', params.dateFilter);
    if (params.groupByEmail) queryParams.append('groupByEmail', String(params.groupByEmail));

    const response = await axios.get(`${API_BASE_URL}/admin/conversations?${queryParams.toString()}`, getAuthHeaders(token));
    return { conversations: response.data.data, total_count: response.data.totalCount };
};

export const getConversationDetails = async (token, conversationId) => {
    const response = await axios.get(`${API_BASE_URL}/admin/conversations/${conversationId}`, getAuthHeaders(token));
    return response.data;
};

export const getGlobalStats = async (token) => {
    const response = await axios.get(`${API_BASE_URL}/admin/stats/global`, getAuthHeaders(token));
    return response.data;
};

export const exportAllConversations = async (token) => {
    const response = await axios.get(`${API_BASE_URL}/admin/export/conversations`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
    });
    const contentDisposition = response.headers['content-disposition'];
    let filename = `franca_conversations_export.json`;
    if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?(.+)"?/);
        if (filenameMatch.length > 1) filename = filenameMatch[1];
    }
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.parentNode.removeChild(link);
    window.URL.revokeObjectURL(url);
};

export const getKbSources = async (token) => {
    const response = await axios.get(`${API_BASE_URL}/admin/kb/sources`, getAuthHeaders(token));
    return response.data;
};

export const getPrivacyAgreements = async (token, skip = 0, limit = 100) => {
    const response = await axios.get(`${API_BASE_URL}/admin/privacy_agreements?skip=${skip}&limit=${limit}`, getAuthHeaders(token));
    return response.data;
};

export const getKbStats = async (token) => {
    const response = await axios.get(`${API_BASE_URL}/admin/kb/stats`, getAuthHeaders(token));
    return response.data;
};

export const ingestFile = async (token, formData) => {
    const response = await axios.post(`${API_BASE_URL}/admin/kb/ingest-file`, formData, {
        headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data',
        },
    });
    return response.data;
};

/**
 * Assigns a team to a user (superadmin only).
 * @param {string} token - The superadmin's authentication token.
 * @param {string} userId - The ID of the user to update.
 * @param {string|null} team - The team to assign to the user, or null to remove team.
 * @returns {Promise<object>} - A promise that resolves to the updated user data.
 */
export const assignUserTeam = async (token, userId, team) => {
    try {
        const response = await axios.put(
            `${API_BASE_URL}/admin/users/${userId}/team`,
            { team },
            getAuthHeaders(token)
        );
        return response.data;
    } catch (error) {
        console.error('Error assigning user team:', error);
        throw error;
    }
};

/**
 * Gets a specific user by ID (admin only).
 * @param {string} token - The admin's authentication token.
 * @param {string} userId - The ID of the user to fetch.
 * @returns {Promise<object>} - A promise that resolves to the user data.
 */
export const getUser = async (token, userId) => {
    try {
        const response = await axios.get(`${API_BASE_URL}/admin/users/${userId}`, getAuthHeaders(token));
        return response.data;
    } catch (error) {
        console.error('Error fetching user:', error);
        throw error;
    }
};

/**
 * Fetches recent generation activity (images and videos combined) - Admin only
 * @param {string} token - Admin authentication token
 * @param {number} limit - Maximum number of items to return
 * @param {number} sinceMinutes - Filter activity from the last N minutes
 * @returns {Promise<Array>} - Array of recent generation activity
 */
export const getRecentGenerations = async (token, limit = 5, sinceMinutes = 60) => {
    const params = new URLSearchParams();
    if (limit) params.append('limit', limit.toString());
    if (sinceMinutes) params.append('since_minutes', sinceMinutes.toString());
    
    try {
        const response = await axios.get(
            `${API_BASE_URL}/admin/generation/recent?${params.toString()}`,
            getAuthHeaders(token)
        );
        return response.data;
    } catch (error) {
        console.error('Error fetching recent generations:', error);
        throw error;
    }
};

/**
 * Fetches aggregate generation statistics - Admin only
 * @param {string} token - Admin authentication token
 * @param {number} sinceHours - Filter stats from the last N hours
 * @param {number} top - Number of top users to return
 * @returns {Promise<Object>} - Aggregate statistics object
 */
export const getGenerationStats = async (token, sinceHours = 24, top = 5) => {
    const params = new URLSearchParams();
    if (sinceHours) params.append('since_hours', sinceHours.toString());
    if (top) params.append('top', top.toString());
    
    try {
        const response = await axios.get(
            `${API_BASE_URL}/admin/generation/stats?${params.toString()}`,
            getAuthHeaders(token)
        );
        return response.data;
    } catch (error) {
        console.error('Error fetching generation stats:', error);
        throw error;
    }
};